import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  Container,
  Heading,
  HStack,
  Center,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import AjudaAcesso from './AjudaAcesso';
import { Logo } from './Logo';

interface LoginFormData {
  email: string;
  senha: string;
}

export const Login: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    senha: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Só depois de UMA tentativa falha. Oferecer as saídas antes de a pessoa tentar é
  // ruído, e empurra para o caminho lento quem ia acertar de primeira.
  const [falhou, setFalhou] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signIn({
        email: formData.email,
        senha: formData.senha,
      });
      
      // Redirecionando para a página de chat
      setTimeout(() => {
        navigate('/app');
      }, 100);
      
    } catch (error) {
      setFalhou(true);
      // Mensagem do SERVIDOR quando houver, e uma frase humana no lugar do
      // "Request failed with status code 401" do axios — que diz a alguém que errou a
      // senha exatamente nada sobre o que fazer.
      const st = (error as any)?.response?.status;
      const doServidor = (error as any)?.response?.data?.error;
      toast({
        title: st === 401 ? 'E-mail ou senha incorretos' : 'Erro ao fazer login',
        description:
          st === 401
            ? 'Confira os dados, ou use uma das opções abaixo do formulário.'
            : (doServidor
               ?? (error instanceof Error && !/status code/i.test(error.message)
                   ? error.message
                   : 'Não foi possível entrar agora. Tente de novo em instantes.')),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="container.sm" py={10}>
      <VStack spacing={8} align="stretch">
        <Center>
          <Logo variant="blue" height="80px" mb={4} />
        </Center>
        <Box p={8} borderWidth={1} borderRadius={8} boxShadow="lg">
          <VStack spacing={4}>
            <Heading size="lg">Login</Heading>
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <FormControl id="email" isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Seu email"
                  />
                </FormControl>

                <FormControl id="senha" isRequired>
                  <FormLabel>Senha</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="senha"
                      value={formData.senha}
                      onChange={handleInputChange}
                      placeholder="Sua senha"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                        icon={showPassword ? <FiEyeOff /> : <FiEye />}
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  fontSize="md"
                  isLoading={loading}
                >
                  Entrar
                </Button>

                <Text textAlign="center" fontSize="sm" color="gray.600">
                  Não tem conta?{' '}
                  <Link to="/register" style={{ color: '#3182ce', fontWeight: 600 }}>
                    Criar conta
                  </Link>
                </Text>
              </Stack>
            </form>

            {falhou && (
              <AjudaAcesso
                email={formData.email}
                aoTrocarConta={() => {
                  setFormData({ email: '', senha: '' });
                  setFalhou(false);
                }}
              />
            )}
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};
